import { zodResolver } from "@hookform/resolvers/zod";
import { Box, LinearProgress, Paper, Typography } from "@mui/material";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import ReservationForm, {
    type ReservationFormData,
    useReservationSchema,
} from "@/components/Owners/ReservationForm.tsx";
import PageHeader from "@/components/PageHeader";
import SuccessToast from "@/components/SuccessToast";
import { useUpdateReservationMutation } from "@/mutations/reservation.ts";
import { useQueryOptionsFactory } from "@/queries";
import type { ListReservation } from "@/queries/reservations.ts";
import type { ListUnit } from "@/queries/units.ts";

export const Route = createFileRoute("/owner/reservation/$resId/edit")({
    component: RouteComponent,
});

function RouteComponent() {
    const navigate = useNavigate();
    const qof = useQueryOptionsFactory();
    const { data: reservationsData } = useSuspenseQuery(qof.reservations.listCurrentAndFuture());
    const { data } = useSuspenseQuery(qof.units.list());
    const { resId } = Route.useParams();
    const reservation = useMemo((): ListReservation | undefined => {
        return reservationsData.data.find((r) => r.id === resId);
    }, [resId, reservationsData.data]);
    const reservations = useMemo(() => {
        return reservationsData.data.filter((r) => r.id !== resId);
    }, [reservationsData.data, resId]);

    useEffect(() => {
        if (reservationsData.data.length > 0 && !reservation) {
            navigate({
                to: "/owner/reservation",
            }).catch((e) => console.error("failed to navigate", e));
        }
    }, [reservation]);

    const units = data.data;

    if (!reservation) {
        return <LinearProgress />;
    }

    return <DisplayComponent reservations={reservations} reservation={reservation} units={units} />;
}

type Props = {
    reservation: ListReservation;
    units: ListUnit[];
    reservations: ListReservation[];
};
const DisplayComponent = ({ reservation, units, reservations }: Props) => {
    const navigate = useNavigate();
    const [showSuccessToast, setShowSuccessToast] = useState(false);
    const reservationSchema = useReservationSchema(reservations);
    const form = useForm<ReservationFormData>({
        resolver: zodResolver(reservationSchema),
        defaultValues: {
            id: reservation.id,
            unitNumber: reservation.calendar.id,
            inputBy: reservation.inputBy,
            customerFirstName: reservation.customerFirstName,
            customerName: reservation.customerName,
            email: reservation.email, //email is phone
            OtherGuests: reservation.OtherGuests,
            startDate: reservation.startDate,
            endDate: reservation.endDate,
            confirmationNumber: reservation.confirmationNumber,
            carMake: reservation.carMaker,
            carLicense: reservation.carLicense,
        },
    });
    const updateReservation = useUpdateReservationMutation();

    const ownerName = units[0]?.owner?.ownerName;

    const unitNumberWatch = form.watch("unitNumber");

    const unitNumber = useMemo(() => {
        const unit = units.find((u) => u.id === unitNumberWatch);
        return unit?.rentalId ?? "";
    }, [unitNumberWatch, units]);

    const onBack = () => {
        navigate({
            to: "/owner/reservation",
        }).catch((e) => console.error("failed to navigate", e));
    };

    useEffect(() => {
        if (updateReservation.data) {
            setShowSuccessToast(true);
            onBack();
        }
    }, [updateReservation.data, navigate]);

    const handleSubmit = (data: ReservationFormData) => {
        updateReservation.mutate({
            attributes: data,
            type: "reservation",
        });
    };

    const instructions = [
        `Edit reservation details for Unit ${unitNumber}`,
        "Update guest information and reservation details",
        "All changes will be saved to the database",
    ];

    return (
        <Box sx={{ maxWidth: "100%" }}>
            <Paper elevation={2} sx={{ borderRadius: 3, overflow: "hidden" }}>
                <PageHeader
                    title={`Edit Reservation - ${ownerName}, Unit ${unitNumber}`}
                    onBack={onBack}
                    backText="← Back to Find Reservation"
                    instructions={instructions}
                />

                <Box
                    sx={{
                        p: { xs: 2, sm: 3, md: 4 },
                        backgroundColor: "white",
                    }}
                >
                    {/* Guest Reservation Information Header */}
                    <Box
                        sx={{
                            mb: 4,
                            pb: 2,
                            borderBottom: "2px solid #e2e8f0",
                        }}
                    >
                        <Typography
                            variant="h5"
                            sx={{
                                fontWeight: 600,
                                color: "#1e293b",
                                mb: 1,
                                fontSize: { xs: "1.25rem", md: "1.5rem" },
                            }}
                        >
                            Edit Guest Reservation Information
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "#64748b",
                                fontSize: "0.9rem",
                            }}
                        >
                            Update the reservation details below
                        </Typography>
                    </Box>
                    <FormProvider {...form}>
                        <ReservationForm
                            units={units}
                            handleSubmit={handleSubmit}
                            disableSubmit={
                                updateReservation.isPending || updateReservation.data !== undefined
                            }
                        />
                    </FormProvider>
                </Box>
            </Paper>

            <SuccessToast
                open={showSuccessToast}
                onClose={() => setShowSuccessToast(false)}
                title="Reservation Updated Successfully!"
                message={`Guest reservation has been updated for Unit ${form.watch("unitNumber") || "selected"}.`}
            />
        </Box>
    );
};
